import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tag, Plus, X } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useCategories } from "@/hooks/useCategories";
import { useOnboarding } from "@/hooks/useOnboarding";

/**
 * CategoryManager - Self-contained category management component
 * Uses useCategories hook for hybrid storage (API for auth users, localStorage for guests)
 * 
 * @param {Object} props
 * @param {string|number} props.productId - Required product ID
 * @param {string} props.size - Optional size variant ("sm" or "default")
 */
export default function CategoryManager({ productId, size = "default" }) {
    const [isOpen, setIsOpen] = useState(false);
    const [newCategory, setNewCategory] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [showNewInput, setShowNewInput] = useState(false);

    const {
        getProductCategories,
        allCategoryNames,
        addCategory,
        removeCategory
    } = useCategories();
    const { completeTask } = useOnboarding();

    const productCategories = getProductCategories(productId);
    const productCategoryNames = productCategories.map(c => c.category_name);

    // Available categories = all unique category names minus those already on this product
    const availableCategories = allCategoryNames.filter(
        cat => !productCategoryNames.includes(cat)
    );

    const handleAddExistingCategory = async (categoryName) => {
        if (productCategoryNames.includes(categoryName)) {
            // console.warn('Category already exists on this product');
            setIsOpen(false);
            return;
        }

        setIsAdding(true);
        try {
            await addCategory(productId, categoryName);
            completeTask('create_collection');
            setIsOpen(false);
        } catch (error) {
            // console.error('Error adding category:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleAddNewCategory = async () => {
        if (!newCategory.trim()) return;

        const trimmedCategory = newCategory.trim();

        if (productCategoryNames.includes(trimmedCategory)) {
            // console.warn('Category already exists on this product');
            setNewCategory("");
            setShowNewInput(false);
            setIsOpen(false);
            return;
        }

        setIsAdding(true);
        try {
            await addCategory(productId, trimmedCategory);
            completeTask('create_collection');
            setNewCategory("");
            setShowNewInput(false);
            setIsOpen(false);
        } catch (error) {
            // console.error('Error adding category:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddNewCategory();
        }
    };

    const handleRemoveCategory = async (categoryId) => {
        try {
            await removeCategory(categoryId);
        } catch (error) {
            // console.error('Error removing category:', error);
        }
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            {productCategories.map((category) => (
                <Badge
                    key={category.id}
                    variant="secondary"
                    className="bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 pr-1"
                >
                    <Tag className="w-3 h-3 mr-1" />
                    {category.category_name}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCategory(category.id);
                        }}
                        className="ml-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded-full p-0.5 transition-colors"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </Badge>
            ))}

            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size={size === "sm" ? "sm" : "default"}
                        className="gap-1 h-7 px-2 text-xs border-dashed border-amber-300 dark:border-amber-600 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 dark:bg-transparent"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Plus className="w-3 h-3" />
                        Collection
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
                    <div className="space-y-3">
                        <h4 className="font-semibold text-sm dark:text-gray-100">Ajouter une catégorie</h4>

                        {/* Catégories existantes */}
                        {availableCategories.length > 0 && !showNewInput && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Catégories existantes:</p>
                                <div className="max-h-48 overflow-y-auto space-y-1">
                                    {availableCategories.map((cat) => (
                                        <button
                                            key={cat}
                                            onClick={() => handleAddExistingCategory(cat)}
                                            disabled={isAdding}
                                            className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/40 dark:text-gray-200 transition-colors flex items-center gap-2 disabled:opacity-50"
                                        >
                                            <Tag className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Bouton pour créer nouvelle catégorie */}
                        {!showNewInput && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowNewInput(true)}
                                className="w-full border-dashed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white dark:bg-transparent"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Créer une nouvelle catégorie
                            </Button>
                        )}

                        {/* Input pour nouvelle catégorie */}
                        {showNewInput && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Nouvelle catégorie:</p>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Ex: Favoris, À acheter..."
                                        value={newCategory}
                                        onChange={(e) => setNewCategory(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                                        disabled={isAdding}
                                        autoFocus
                                    />
                                    <Button
                                        size="sm"
                                        onClick={handleAddNewCategory}
                                        disabled={!newCategory.trim() || isAdding}
                                        className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setShowNewInput(false);
                                        setNewCategory("");
                                    }}
                                    className="w-full text-xs dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                                >
                                    Annuler
                                </Button>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}